import React, { useId, useMemo, useState } from 'react';
import {
  Area, Bar, CartesianGrid, Cell, ComposedChart, LabelList, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { formatCurrency, formatDate, formatNumber } from '../../utils/format';
import { cn } from '../../utils/cn';
import { chartDef, type ChartDef, type SeriesDef, type ValueFormat } from './chartRegistry';
import { useAllChartSettings, type AxisSettings, type ChartSettings, type LabelPosition, type SeriesSettings, type SeriesType } from './chartSettings';

// Every chart in the web app draws through this one component: the chart's
// defaults (chartRegistry.ts) with the Admin's changes from Administration
// -> Appearance -> Charts laid over them - so the settings page's preview is
// exactly what the real page shows.

export function pick(row: unknown, path: string): number | null {
  const v = path.split('.').reduce<any>((o, k) => (o == null ? o : o[k]), row);
  return v == null || v === '' || Number.isNaN(Number(v)) ? null : Number(v);
}

export function formatValue(kind: ValueFormat | undefined, v: number, decimals: number | null | undefined, forLabel: boolean): string {
  const d = (fallback: number) => (decimals ?? fallback);
  switch (kind) {
    case 'litres': return forLabel ? formatNumber(v, d(1)) : `${formatNumber(v, d(1))} L`;
    case 'currency': return decimals != null ? `₹${formatNumber(v, decimals)}` : formatCurrency(v);
    case 'percent': return forLabel ? formatNumber(v, d(2)) : `${formatNumber(v, d(2))}%`;
    case 'cows': return `${formatNumber(v, d(0))} cows`;
    case 'kg': return forLabel ? formatNumber(v, d(0)) : `${formatNumber(v, d(1))} kg`;
    case 'rate': return forLabel ? formatNumber(v, d(0)) : `₹${formatNumber(v, d(2))}/L`;
    default: return formatNumber(v, d(1));
  }
}

const DASH: Record<string, string | undefined> = { solid: undefined, dashed: '6 3', dotted: '2 3', dashdot: '8 3 2 3' };
const CURVE = { smooth: 'monotone', straight: 'linear', step: 'step' } as const;
const UNITS: Record<string, [number, string]> = { none: [1, ''], thousands: [1e3, 'k'], lakhs: [1e5, ' lakh'], crores: [1e7, ' Cr'] };

interface Resolved {
  def: SeriesDef;
  idx: number;
  key: string;
  name: string;
  type: SeriesType;
  color: string;
  axis: 'left' | 'right';
  hidden: boolean;
  width: number;
  dash?: string;
  curve: 'monotone' | 'linear' | 'step';
  marker: NonNullable<SeriesSettings['marker']>;
  markerSize: number;
  opacity: number;
  gradient: boolean;
  labelsOn: boolean;
  labelPosition: LabelPosition;
  labelColor?: string;
  altColor?: string;
  trend: NonNullable<SeriesSettings['trend']>;
  trendPeriod: number;
  stack?: string;
}

function resolveSeries(def: SeriesDef, idx: number, ss: SeriesSettings = {}, labelMode: string): Resolved {
  const type = ss.type ?? def.type;
  const labels = ss.labels ?? 'inherit';
  return {
    def, idx, key: def.key, type,
    name: ss.name || def.name,
    color: ss.color || def.color,
    axis: ss.axis ?? def.axis ?? 'left',
    hidden: !!ss.hidden,
    width: ss.width ?? def.width ?? 2,
    dash: DASH[ss.dash ?? def.dash ?? 'solid'],
    curve: CURVE[ss.curve ?? 'smooth'],
    marker: ss.marker ?? (type === 'points' ? 'circle' : 'circle'),
    markerSize: ss.markerSize ?? (type === 'points' ? 4 : 3),
    opacity: ss.opacity ?? (type === 'area' ? 35 : 100),
    gradient: !!ss.gradient,
    labelsOn: labels === 'on' || (labels === 'inherit' && labelMode !== 'none' && def.labels !== false),
    labelPosition: ss.labelPosition ?? def.labelPosition ?? 'auto',
    labelColor: ss.labelColor || def.labelColor,
    altColor: def.alt ? ss.altColor || def.alt.color : undefined,
    trend: ss.trend ?? 'none',
    trendPeriod: Math.max(2, ss.trendPeriod ?? 3),
    stack: type === 'bar' || type === 'area' ? def.stack : undefined,
  };
}

function trendValues(values: (number | null)[], kind: 'linear' | 'moving', period: number): (number | null)[] {
  const pts = values.map((v, i) => [i, v] as const).filter((p): p is readonly [number, number] => p[1] != null);
  if (pts.length < 2) return values.map(() => null);
  if (kind === 'moving') {
    return values.map((_, i) => {
      const win = pts.filter(([j]) => j <= i).slice(-period);
      return win.length === period && values[i] != null ? win.reduce((a, [, v]) => a + v, 0) / period : null;
    });
  }
  const n = pts.length;
  const sx = pts.reduce((a, [x]) => a + x, 0);
  const sy = pts.reduce((a, [, y]) => a + y, 0);
  const sxx = pts.reduce((a, [x]) => a + x * x, 0);
  const sxy = pts.reduce((a, [x, y]) => a + x * y, 0);
  const slope = (n * sxy - sx * sy) / (n * sxx - sx * sx || 1);
  const icept = (sy - slope * sx) / n;
  return values.map((_, i) => Math.round((icept + slope * i) * 100) / 100);
}

function decimalsOf(step: number): number {
  const s = String(step);
  return s.includes('.') ? Math.min(4, s.split('.')[1].length) : 0;
}

// Light text on a dark bar, dark text on a light one.
function contrastText(hex: string): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return '#ffffff';
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
  return (0.299 * r + 0.587 * g + 0.114 * b) > 150 ? '#1f3d22' : '#ffffff';
}

function HaloText({ x, y, fill, size, halo = true, weight = 600, children }: { x: number; y: number; fill: string; size: number; halo?: boolean; weight?: number; children: React.ReactNode }) {
  return (
    <text x={x} y={y} textAnchor="middle" fontSize={size} fontWeight={weight} fill={fill} stroke={halo ? 'white' : undefined} strokeWidth={halo ? 3 : undefined} paintOrder="stroke" style={{ pointerEvents: 'none' }}>
      {children}
    </text>
  );
}

function Marker({ cx, cy, shape, size, color }: { cx?: number; cy?: number; shape: Resolved['marker']; size: number; color: string }) {
  if (cx == null || cy == null || shape === 'none') return null;
  const s = size;
  if (shape === 'square') return <rect x={cx - s} y={cy - s} width={s * 2} height={s * 2} fill={color} stroke="#fff" strokeWidth={1} />;
  if (shape === 'diamond') return <polygon points={`${cx},${cy - s * 1.3} ${cx + s * 1.3},${cy} ${cx},${cy + s * 1.3} ${cx - s * 1.3},${cy}`} fill={color} stroke="#fff" strokeWidth={1} />;
  if (shape === 'triangle') return <polygon points={`${cx},${cy - s * 1.3} ${cx + s * 1.2},${cy + s} ${cx - s * 1.2},${cy + s}`} fill={color} stroke="#fff" strokeWidth={1} />;
  return <circle cx={cx} cy={cy} r={s} fill={color} stroke="#fff" strokeWidth={1} />;
}

function parseRefLines(text: string | undefined): { y: number; label: string }[] {
  if (!text) return [];
  return text.split(/[,;\n]/).map((part) => part.trim().match(/^(-?\d+(?:\.\d+)?)\s*(.*)$/)).filter((m): m is RegExpMatchArray => !!m)
    .map((m) => ({ y: Number(m[1]), label: m[2] ?? '' }));
}

interface AxisBuild {
  domain: any[];
  ticks?: number[];
  allowDataOverflow?: boolean;
  scale?: 'log' | 'auto';
  tickFormatter: (v: number) => string;
  lo?: number;
  hi?: number;
}

function buildAxis(a: AxisSettings & { integer?: boolean; headroom?: number; minMax?: number }, values: number[], percent: boolean): AxisBuild {
  const [div, unitSuffix] = UNITS[a.units ?? 'none'] ?? UNITS.none;
  const fmt = (v: number, dec: number) => `${a.prefix ?? ''}${formatNumber(v / div, dec)}${unitSuffix}${a.suffix ?? ''}`;
  if (percent) {
    return { domain: [0, 1], ticks: [0, 0.25, 0.5, 0.75, 1], tickFormatter: (v) => `${Math.round(v * 100)}%`, lo: 0, hi: 1 };
  }
  const dataMin = values.length ? Math.min(...values) : 0;
  const dataMax = values.length ? Math.max(...values) : 0;
  const hasMin = a.min != null;
  const hasMax = a.max != null;
  if (a.log) {
    return {
      domain: [hasMin && a.min! > 0 ? a.min : 'auto', hasMax ? a.max : 'auto'], scale: 'log', allowDataOverflow: true,
      tickFormatter: (v) => fmt(v, a.decimals ?? (Number.isInteger(v / div) ? 0 : 1)),
    };
  }
  const step = a.step && a.step > 0 ? a.step : null;
  if (step) {
    const floorTo = (v: number) => Math.floor(v / step + 1e-9) * step;
    const ceilTo = (v: number) => Math.ceil(v / step - 1e-9) * step;
    let lo = hasMin ? (a.clip ? a.min! : Math.min(a.min!, floorTo(dataMin))) : (dataMin >= 0 ? 0 : floorTo(dataMin));
    let hi = hasMax ? (a.clip ? a.max! : Math.max(a.max!, ceilTo(dataMax))) : ceilTo(dataMax);
    if (hi <= lo) hi = lo + step;
    const count = Math.round((hi - lo) / step);
    if (count <= 400) {
      const dec = a.decimals ?? decimalsOf(step);
      // Not told how often to label: about a dozen labels at most, so they never pile up.
      const every = Math.max(1, a.labelEvery ?? (count > 24 ? Math.ceil(count / 12) : 1));
      const ticks = Array.from({ length: count + 1 }, (_, i) => Math.round((lo + i * step) * 1e6) / 1e6);
      lo = ticks[0];
      hi = ticks[ticks.length - 1];
      return {
        domain: [lo, hi], ticks, allowDataOverflow: !!a.clip, lo, hi,
        tickFormatter: (v) => (Math.round((v - lo) / step) % every === 0 ? fmt(v, dec) : ''),
      };
    }
  }
  const hiAuto = a.headroom != null ? (max: number) => Math.max(max + a.headroom!, a.minMax ?? 0) : 'auto';
  const lo = hasMin ? a.min : (dataMin >= 0 ? 0 : 'auto');
  const hi = hasMax ? a.max : hiAuto;
  return {
    domain: [lo, hi], allowDataOverflow: !!a.clip,
    lo: typeof lo === 'number' ? lo : dataMin, hi: typeof hi === 'number' ? hi : dataMax,
    tickFormatter: (v) => fmt(v, a.decimals ?? (a.integer || Number.isInteger(v / div) ? 0 : 1)),
  };
}

export interface SmartChartProps {
  /** A chart from chartRegistry.ts - or pass `def` directly (the settings preview). */
  id?: string;
  def?: ChartDef;
  data: any[];
  /** Settings to draw with instead of the saved ones (the settings preview). */
  settings?: ChartSettings;
}

export function SmartChart({ id, def: defProp, data, settings: settingsProp }: SmartChartProps) {
  const all = useAllChartSettings();
  const def = defProp ?? chartDef(id!);
  const s: ChartSettings = settingsProp ?? all[def.id] ?? {};
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const [legendOff, setLegendOff] = useState<Record<string, boolean>>({});

  const stacking = s.stacking ?? 'stacked';
  const rawMode = s.labelMode ?? def.labelMode;
  const labelMode = rawMode === 'auto' ? (data.length <= 14 ? 'all' : 'last') : rawMode;
  const fontSize = s.fontSize ?? 11;
  const labelSize = s.labelSize ?? Math.max(9, fontSize - 1);
  const halo = s.labelHalo ?? true;
  const animate = s.animation ?? true;

  const series = useMemo(
    () => def.series
      .filter((sd) => !sd.optional || data.some((r) => (pick(r, sd.key) ?? 0) !== 0))
      .map((sd, i) => resolveSeries(sd, i, s.series?.[sd.key], rawMode)),
    [def, data, s.series, rawMode]
  );
  const shown = series.filter((r) => !r.hidden);
  const drawn = shown.filter((r) => !legendOff[r.key]);
  const stackedKey = (r: Resolved) => (stacking !== 'clustered' ? r.stack : undefined);

  // Trend lines are extra columns on each row.
  const rows = useMemo(() => {
    const withTrend = shown.filter((r) => r.trend !== 'none');
    if (!withTrend.length) return data;
    const cols = withTrend.map((r) => [r.idx, trendValues(data.map((row) => pick(row, r.key)), r.trend as 'linear' | 'moving', r.trendPeriod)] as const);
    return data.map((row, i) => {
      const extra: Record<string, number | null> = {};
      cols.forEach(([idx, vals]) => { extra[`__trend${idx}`] = vals[i]; });
      return { ...row, ...extra };
    });
  }, [data, shown]);

  // The values each axis must fit - a stack counts as its total.
  const axisValues = (axis: 'left' | 'right'): number[] => {
    const out: number[] = [];
    for (const row of rows) {
      const stacks: Record<string, number> = {};
      for (const r of drawn.filter((x) => x.axis === axis)) {
        const v = pick(row, r.key);
        if (v == null) continue;
        const st = stackedKey(r);
        if (st) stacks[st] = (stacks[st] ?? 0) + v;
        else out.push(v);
        if (r.trend !== 'none') {
          const t = (row as any)[`__trend${r.idx}`];
          if (t != null) out.push(t);
        }
      }
      out.push(...Object.values(stacks));
    }
    return out;
  };
  const percentOn = (axis: 'left' | 'right') => stacking === 'percent' && drawn.some((r) => r.axis === axis && stackedKey(r));
  const leftCfg = { ...def.left, ...(s.left ?? {}) };
  const rightCfg = { ...(def.right ?? {}), ...(s.right ?? {}) };
  const left = buildAxis(leftCfg, axisValues('left'), percentOn('left'));
  const rightUsed = shown.some((r) => r.axis === 'right');
  const right = buildAxis(rightCfg, axisValues('right'), percentOn('right'));

  // The top-most bar of each stack carries the rounded corners and the total.
  const stackTop: Record<string, string> = {};
  drawn.forEach((r) => { const st = stackedKey(r); if (st && r.type === 'bar') stackTop[st] = r.key; });
  const showTotal = (s.showTotal ?? def.showTotal ?? false) && stacking === 'stacked';
  const radius = s.barRadius ?? def.barRadius;

  const lastIndex = (key: string) => {
    for (let i = rows.length - 1; i >= 0; i--) if (pick(rows[i], key) != null) return i;
    return -1;
  };
  const labelFor = (r: Resolved) => {
    const last = lastIndex(r.key);
    return (props: any) => {
      const { x, y, width = 0, height = 0, value, index } = props;
      if (value == null || x == null || y == null) return null;
      const v = Number(value);
      if (r.type === 'bar' && !v) return null;
      if (labelMode === 'last' && index !== last) return null;
      const text = formatValue(r.def.format, v, s.labelDecimals, true);
      const isBar = r.type === 'bar';
      const cx = isBar ? x + width / 2 : x;
      let pos = r.labelPosition;
      if (pos === 'auto') pos = isBar ? 'top' : 'auto';
      const inside = isBar && (pos === 'center' || pos === 'insideTop');
      if (inside && Math.abs(height) < labelSize + 2) return null;
      const color = r.labelColor || (inside ? contrastText(r.color) : isBar ? '#33392f' : r.color);
      let ty: number;
      if (isBar) ty = pos === 'center' ? y + height / 2 + labelSize / 3 : pos === 'insideTop' ? y + labelSize + 2 : pos === 'bottom' ? y + height - 4 : y - 6;
      else ty = y + (pos === 'top' ? -10 : pos === 'bottom' ? 16 : pos === 'center' ? 4 : r.def.labelDy ?? -10);
      return <HaloText x={cx} y={ty} fill={color} size={labelSize} halo={halo && !inside}>{text}</HaloText>;
    };
  };
  const totalFor = (stack: string) => (props: any) => {
    const { x, y, width = 0, index } = props;
    const row = rows[index];
    const members = drawn.filter((r) => stackedKey(r) === stack && r.type === 'bar');
    const total = members.reduce((a, r) => a + (pick(row, r.key) ?? 0), 0);
    if (!total || x == null || y == null) return null;
    if (labelMode === 'last' && index !== rows.length - 1) return null;
    return <HaloText x={x + width / 2} y={y - 8} fill="#33392f" size={labelSize} weight={700} halo={halo}>{formatValue(members[0]?.def.format, total, s.labelDecimals, true)}</HaloText>;
  };

  const xFmt = (v: unknown) => (def.xFormat === 'date' ? formatDate(String(v)) : String(v ?? ''));
  const xAngle = s.xAngle ?? def.xAngle ?? 0;
  const xTitle = s.xTitle ?? '';
  const xHeight = (xAngle ? Math.min(110, 40 + Math.abs(xAngle) * 1.2) : 30) + (xTitle ? 16 : 0);
  const anyLabels = drawn.some((r) => r.labelsOn) || showTotal;
  const majorEvery = s.majorEvery !== undefined ? s.majorEvery : def.majorEvery;
  const majors: number[] = [];
  if (majorEvery && majorEvery > 0 && left.lo != null && left.hi != null && (left.hi - left.lo) / majorEvery <= 100) {
    for (let m = Math.ceil(left.lo / majorEvery - 1e-9) * majorEvery; m <= left.hi + 1e-9; m += majorEvery) majors.push(Math.round(m * 1e6) / 1e6);
  }
  const refLines = parseRefLines(s.refLines);
  const seriesOf = (dataKey: string) => series.find((r) => r.key === dataKey || `__trend${r.idx}` === dataKey);
  const fillOf = (r: Resolved) => (r.gradient ? `url(#${uid}g${r.idx})` : r.color);
  const legendShown = s.legend ?? def.legend;
  const legendPos = s.legendPosition ?? 'bottom';
  const title = s.title || def.title;
  const showTitle = s.showTitle ?? def.showTitle;

  const legend = legendShown && (
    <div className={cn('flex flex-wrap items-center justify-center gap-x-4 gap-y-1', (legendPos === 'left' || legendPos === 'right') && 'flex-col items-start justify-center')} style={{ fontSize: fontSize + 1 }}>
      {shown.map((r) => (
        <React.Fragment key={r.key}>
          <button
            type="button"
            onClick={() => setLegendOff((p) => ({ ...p, [r.key]: !p[r.key] }))}
            title="Click to show / hide"
            className="inline-flex items-center gap-1.5 text-ink-700"
            style={{ opacity: legendOff[r.key] ? 0.4 : 1 }}
          >
            {r.type === 'bar' || r.type === 'area'
              ? <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: r.color }} />
              : <span className="inline-block h-0.5 w-4" style={{ background: r.color }} />}
            {r.name}
          </button>
          {r.altColor && r.def.alt && (
            <span className="inline-flex items-center gap-1.5 text-ink-700">
              <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: r.altColor }} />{r.def.alt.label}
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const chart = (
    <ResponsiveContainer width="100%" height={s.height ?? def.height}>
      <ComposedChart
        data={rows}
        margin={{ top: anyLabels ? 28 : 10, right: 10, left: 0, bottom: 5 }}
        stackOffset={stacking === 'percent' ? 'expand' : undefined}
        barCategoryGap={`${s.groupGap ?? 10}%`}
        barGap={s.barGap ?? 4}
      >
        <defs>
          {shown.filter((r) => r.gradient).map((r) => (
            <linearGradient key={r.idx} id={`${uid}g${r.idx}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={r.color} stopOpacity={0.95} />
              <stop offset="100%" stopColor={r.color} stopOpacity={0.3} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid
          horizontal={s.gridH ?? true}
          vertical={s.gridV ?? def.gridV}
          strokeDasharray={(s.gridDashed ?? def.gridDashed) ? '3 3' : undefined}
          stroke={s.gridColor || '#a2a89b33'}
        />
        {majors.map((m) => <ReferenceLine key={`m${m}`} yAxisId="left" y={m} stroke="#a2a89b80" />)}
        {refLines.map((r, i) => (
          <ReferenceLine key={`r${i}`} yAxisId="left" y={r.y} stroke={s.refColor || '#d6402f'} strokeDasharray="5 3" strokeWidth={1.5}
            label={r.label ? { value: r.label, position: 'insideTopRight', fontSize: labelSize, fill: s.refColor || '#d6402f' } : undefined} />
        ))}
        <XAxis
          dataKey={def.xKey}
          tickFormatter={xFmt}
          tick={{ fontSize }}
          hide={s.xShow === false}
          reversed={!!s.xReverse}
          interval={(s.xAllLabels ?? def.xAllLabels) ? 0 : 'preserveEnd'}
          angle={xAngle}
          textAnchor={xAngle ? 'end' : 'middle'}
          height={xHeight}
          label={xTitle ? { value: xTitle, position: 'insideBottom', offset: 0, fontSize } : undefined}
        />
        <YAxis
          yAxisId="left"
          tick={{ fontSize: fontSize - (left.ticks && left.ticks.length > 20 ? 1 : 0) }}
          width={(def.left.width ?? 40) + (leftCfg.title ? 12 : 0)}
          hide={leftCfg.show === false}
          domain={left.domain}
          ticks={left.ticks}
          interval={left.ticks ? 0 : undefined}
          scale={left.scale ?? 'auto'}
          allowDataOverflow={left.allowDataOverflow}
          allowDecimals={!def.left.integer}
          reversed={!!leftCfg.reverse}
          tickFormatter={left.tickFormatter}
          label={leftCfg.title ? { value: leftCfg.title, angle: -90, position: 'insideLeft', fontSize } : undefined}
        />
        {rightUsed && (
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize }}
            width={(def.right?.width ?? 44) + (rightCfg.title ? 12 : 0)}
            hide={rightCfg.show === false}
            domain={right.domain}
            ticks={right.ticks}
            interval={right.ticks ? 0 : undefined}
            scale={right.scale ?? 'auto'}
            allowDataOverflow={right.allowDataOverflow}
            allowDecimals={!def.right?.integer}
            reversed={!!rightCfg.reverse}
            tickFormatter={right.tickFormatter}
            label={rightCfg.title ? { value: rightCfg.title, angle: 90, position: 'insideRight', fontSize } : undefined}
          />
        )}
        {s.tooltip !== false && (
          <Tooltip
            cursor={{ fill: '#a2a89b1f' }}
            labelFormatter={xFmt}
            formatter={(value: number, name: string, item: any) => {
              const r = seriesOf(String(item?.dataKey ?? ''));
              if (value == null) return ['—', name];
              const text = formatValue(r?.def.format, Number(value), s.labelDecimals, false);
              const extra = r && item?.dataKey === r.key ? def.tooltipExtra?.(item?.payload, r.key) : undefined;
              return [extra ? `${text} · ${extra}` : text, name];
            }}
          />
        )}
        {series.filter((r) => !r.hidden).map((r) => {
          const off = !!legendOff[r.key];
          const common = { yAxisId: r.axis, dataKey: r.key, name: r.name, hide: off, isAnimationActive: animate };
          const labels = r.labelsOn && !off ? <LabelList dataKey={r.key} content={labelFor(r)} /> : null;
          if (r.type === 'bar') {
            const st = stackedKey(r);
            const top = !st || stackTop[st] === r.key;
            return (
              <Bar key={r.key} {...common} stackId={st} fill={fillOf(r)} fillOpacity={r.opacity / 100} radius={top && radius ? [radius, radius, 0, 0] : undefined}
                maxBarSize={s.maxBarSize ?? def.maxBarSize}>
                {r.altColor && r.def.alt && rows.map((row, i) => <Cell key={i} fill={r.def.alt!.when(row) ? r.altColor : fillOf(r)} />)}
                {labels}
                {showTotal && st && top && !off && <LabelList dataKey={r.key} content={totalFor(st)} />}
              </Bar>
            );
          }
          if (r.type === 'area') {
            return (
              <Area key={r.key} {...common} type={r.curve} stackId={stackedKey(r)} stroke={r.color} strokeWidth={r.width} strokeDasharray={r.dash}
                fill={fillOf(r)} fillOpacity={r.opacity / 100} connectNulls
                dot={r.marker === 'none' ? false : (p: any) => <Marker key={p.index} cx={p.cx} cy={p.cy} shape={r.marker} size={r.markerSize} color={r.color} />}>
                {labels}
              </Area>
            );
          }
          return (
            <Line key={r.key} {...common} type={r.curve} stroke={r.type === 'points' ? 'none' : r.color} strokeWidth={r.width} strokeDasharray={r.dash} connectNulls
              dot={r.marker === 'none' ? false : (p: any) => <Marker key={p.index} cx={p.cx} cy={p.cy} shape={r.marker} size={r.markerSize} color={r.color} />}
              activeDot={{ r: r.markerSize + 2 }}>
              {labels}
            </Line>
          );
        })}
        {shown.filter((r) => r.trend !== 'none' && !legendOff[r.key]).map((r) => (
          <Line key={`t${r.idx}`} yAxisId={r.axis} dataKey={`__trend${r.idx}`} name={`${r.name} — ${r.trend === 'linear' ? 'trend' : `${r.trendPeriod}-point average`}`}
            stroke={r.color} strokeWidth={1.5} strokeDasharray="4 4" dot={false} activeDot={false} connectNulls legendType="none" isAnimationActive={animate} />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );

  return (
    <div
      className={cn((s.border || s.background) && 'rounded-md p-2', s.border && 'border border-ink-300/40')}
      style={s.background ? { background: s.background } : undefined}
    >
      {showTitle && <p className="text-card-title mb-1">{title}</p>}
      {legendShown && legendPos === 'top' && <div className="mb-1">{legend}</div>}
      {legendShown && (legendPos === 'left' || legendPos === 'right') ? (
        <div className={cn('flex items-center gap-3', legendPos === 'left' && 'flex-row-reverse')}>
          <div className="flex-1 min-w-0">{chart}</div>
          <div className="shrink-0 max-w-[30%]">{legend}</div>
        </div>
      ) : chart}
      {legendShown && legendPos === 'bottom' && <div className="mt-1">{legend}</div>}
      {s.dataTable && (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full border-collapse text-helper-text tabular-nums">
            <thead>
              <tr>
                <th className="border border-ink-300/20 px-1.5 py-1 text-left" />
                {rows.map((row, i) => <th key={i} className="border border-ink-300/20 px-1.5 py-1 text-right font-medium whitespace-nowrap">{xFmt(row[def.xKey])}</th>)}
              </tr>
            </thead>
            <tbody>
              {drawn.map((r) => (
                <tr key={r.key}>
                  <td className="border border-ink-300/20 px-1.5 py-1 whitespace-nowrap">
                    <span className="mr-1.5 inline-block h-2 w-2 rounded-sm" style={{ background: r.color }} />{r.name}
                  </td>
                  {rows.map((row, i) => {
                    const v = pick(row, r.key);
                    return <td key={i} className="border border-ink-300/20 px-1.5 py-1 text-right">{v == null ? '—' : formatValue(r.def.format, v, s.labelDecimals, true)}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
